import React, { useEffect, useState, Suspense, lazy, useRef } from 'react'
import LayoutSidebar from '../../../components/LayoutSidebar'
import VerificationQuestions from "./VerificationQuestions"
import VerificationPaymentSchedule from "./VerificationPaymentSchedule"
import NoteDashlet from "../../dashlets/NoteDashlet"
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useRequest } from '../../../hooks/UseRequest'
import { useDispatch, useSelector } from 'react-redux'
import { setNotification } from '../../../redux/notificationSlice'
import Loading from '../../../components/Loading'
import CustomerServiceButtons from "../../../components/CustomerServiceButtons"
import { selectAuth } from "../../../redux/authSlice"
import { selectUser } from '../../../redux/userSlice'
import VerificationDashlet from "../../dashlets/VerificationDashlet";
import LeadSignatureDashlet from "../../dashlets/LeadSignatureDashlet";
import NoteButton from '../../../components/elements/NoteButton'
import { selectDropdowns, selectDropdownOptions } from '../../../redux/dropdownSlice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHistory, faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import { setModal, selectModal } from "../../../redux/modalSlice"
import VerificationLoanSigning from "./VerificationLoanSigning"
import { hasUserPermission } from '../../../utils/permissionHelper'
import LockNotice from '../../../components/elements/LockNotice'
import { useGetBanners } from "../../../utils/detailViewHelper";
import { selectSubpanelRerender, triggerRerender } from '../../../redux/rerenderSlice'
import BankChangeDashlet from '../../dashlets/BankChangeDashlet'
import { FormatISODateTime, FormatDateTimeToSame } from '../../../helpers/formatting'
import HolidayDashlet from '../../dashlets/HolidayDashlet'
import { selectModules } from '../../../redux/moduleSlice'
import PortfolioDashlet from '../../dashlets/PortfolioDashlet'

const ModuleSubPanel = lazy(() => import('../../modules/ModuleSubPanel'));

/**
 * Base Structure for Custom Verifications Agent Flow
 * @constructor
 */
export default function Verification() {
  const location = useLocation()
  const navi = useNavigate()
  const params = useParams()
  const requests = useRequest()
  const dispatch = useDispatch()

  // Redux Data
  const auth = useSelector(selectAuth)
  const dropdowns = useSelector(selectDropdowns)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const modal = useSelector(selectModal)
  const active = useSelector(selectUser)
  const modules = useSelector(selectModules)
  // Use this selector to get the rerender count for manual_verification. The component will rerender whenever the count changes.
  const rerenderValue = useSelector((state) => selectSubpanelRerender(state, 'manual_verification'));

  // Base Flow State
  const [step, setStep] = useState('questions')
  // Used for viewing historical verification flows based on lead GUID
  const queryParams = new URLSearchParams(location.search);
  const [customerData, setCustomerData] = useState({})
  const [currentPtiTerms, setCurrentPtiTerms] = useState({})
  const [promotionName, setPromotionName] = useState(null)
  const [programs, setPrograms] = useState([])
  const [startVerification, setStartVerification] = useState(false)
  const [verificationId, setVerificationId] = useState(0)
  const userStartedVerificationId = useRef(null) // Track the verification ID set by user action
  const [saving, setSaving] = useState(false)
  const [managerApproval, setManagerApproval] = useState({})
  const [managerApprovalPoll, setManagerApprovalPoll] = useState(false)
  const [bankChangeApproval, setBankChangeApproval] = useState({})
  const [bankChangeApprovalPoll, setBankChangeApprovalPoll] = useState(false)
  const [canStartVerification, setCanStartVerification] = useState(false)

  const [userPermissions, setUserPermissions] = useState()
  const [module, setModule] = useState()
  const [parentModule, setParentModule] = useState()

  const [isReadOnly, setIsReadOnly] = useState(true)
  const [isVerified, setIsVerified] = useState(false)

  // Agent Lock State
  const [displayLockNotice, setDisplayLockNotice] = useState(false)
  const [lockNotificationText, setLockNotificationText] = useState()
  const [lockAgentUserId, setLockAgentUserId] = useState()
  const [forceRecordUnlockPermission, setForceRecordUnlockPermission] = useState(false)
  const [lockCheck, setLockCheck] = useState(false)

  // Verification Question: Step 1 State
  const [paymentMethods, setPaymentMethods] = useState([])
  const [verificationFields, setVerificationFields] = useState([])
  const [nextPaymentDatesTooltip, setNextPaymentDatesTooltip] = useState('')

  // Verification Payment Schedule: Step 2 State
  const [paymentScheduleDetails, setPaymentScheduleDetails] = useState({})
  const [latestVerificationBankAccountId, setLatestVerificationBankAccountId] = useState(null)
  const [paymentScheduleData, setPaymentScheduleData] = useState(null)
  const [ptiTerms, setPtiTerms] = useState({})
  const [isPrequalified, setIsPrequalified] = useState(false)
  const [prequalifiedData, setPrequalifiedData] = useState(null)
  const [isRealtimeFundingAvailable, setIsRealtimeFundingAvailable] = useState()
  const [originalEffectiveDate, setOriginalEffectiveDate] = useState(null)
  const [originalLoanFundingMethodId, setOriginalLoanFundingMethodId] = useState(null)

  // Verification Loan Agreement: Step 3 State
  const [loanData, setLoanData] = useState(null); // Data for the mocked loan signing page

  // @TODO: What are these pieces of state? Likely should be in a useEffect?
  const verificationField = dropdowns.data.filter((o) => o.code_key === 'verification_field')[0]
  const verificationFieldOptions = dropdownOptions.filter((o) => o.dropdown_id === verificationField.id).map((o) => ({ codeKey: o.code_key, id: o.id, isLoading: false, isEditing: false }))

  const verificationFieldsObject = verificationFieldOptions.reduce((obj, item) => {
    obj[item.codeKey] = item;
    return obj;
  }, {});
  const { getBanners, isLoadingBanners, banners } = useGetBanners();

  useEffect(() => {
    const ps = modules.data.find((e) => e.code_key === 'manual_verification')
    if (ps) {
      setModule(ps)
    }

    const m = modules.data.find((e) => e.code_key === 'customer')
    if (m) {
      setParentModule(m)
    } else {
      navi('/404')
    }
  }, [modules.data, params.module_name])

  /**
   * Check if the user has correct permissions
   */
  useEffect(() => {
    if (active.status === 'succeeded') {
      const activePermissions = active.spoofed_user.user_permission_options ? active.spoofed_user.user_permission_options : active.data.user_permission_options
      setUserPermissions(activePermissions)
      if (hasUserPermission(activePermissions, `force_record_unlock`)) {
        setForceRecordUnlockPermission(true)
      }
    }
  }, [active, module])

  /**
   * Call LOX Api to get the details for the latest lead for this customer. This is the main data call for manual verification
   */
  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (auth.accessToken) {
      getLatestLead()
      getNextPaymentDates()
    }
  }, [auth.accessToken, loanData])

  /**
   * Call LOX Api to get the list of available programs for a customer.
   */
  useEffect(() => {
    if (Object.keys(customerData).length > 0) {
      getPrograms()
    }
  }, [customerData, paymentScheduleDetails])

  /**
   * This useEffect drives the re-render logic for manual verification when another element, for example a quick action,
   *  wants to force a rerender. This looks like it is a duplicate of the main latestLead call, but
   *  this has to be separate because we only want this to happen when re-render changes and is greater than 0.
   */
  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (auth.accessToken && (rerenderValue > 0)) {
      getLatestLead()
    }
  }, [rerenderValue, auth.accessToken])

  /**
   * This useEffect drives the re-render logic for manual verification when another element, for example a quick action,
   *  wants to force a rerender. This looks like it is a duplicate of the program call, but
   *  this has to be separate because we only want this to happen when re-render changes and is greater than 0.
   */
  useEffect(() => {
    if (Object.keys(customerData).length > 0 && rerenderValue > 0) {
      getPrograms()
    }
  }, [customerData, rerenderValue])

  /**
   * When switching between steps it is common for the agent to be at the bottom of the main content. This useEffect scrolls
   * the screen back to the top, so that the first thing the Agent sees is the top of the new content. We have to do this
   * because we are not actually switching pages, just re-rendering in the same page.
   */
  useEffect(() => {
    const section = document.querySelector('#main_content');
    section.scrollTo({ top: 0 })
  }, [step])

  /**
   * On page load create a timeout that will run every 15 seconds to determine the status of any in progress manager approvals.
   * Only run this useEffect if there is an actual pending manager approval to save DB hits.
   */
  useEffect(() => {
    let timeoutId = 0;

    const managerApprovalStatus = dropdownOptions.find((o) => o?.id === managerApproval?.mgr_approval_status_dropdown_option_id)?.code_key
    if (managerApprovalPoll && managerApprovalStatus === 'mgr_approval_status_required') {
      getManagerApprovalStatus();
      const startLoop = () => {
        timeoutId = setTimeout(() => {
          getManagerApprovalStatus();
          startLoop();
        }, 15000);
      };
      startLoop();
    }

    return () => clearTimeout(timeoutId)
  }, [managerApprovalPoll])

  /**
   * On page load create a timeout that will run every 15 seconds to determine the status of any in progress manager approvals.
   * Only run this useEffect if there is an actual pending manager approval to save DB hits.
   */
  useEffect(() => {
    let timeoutId = 0;

    const bankChangeApprovalStatus = dropdownOptions.find((o) => o?.id === bankChangeApproval?.bank_change_outcome_dropdown_option_id)?.code_key
    if (bankChangeApprovalPoll && bankChangeApprovalStatus === 'bank_change_outcome_duplicate_pending') {
      getBankChangeApprovalStatus();
      const startLoop = () => {
        timeoutId = setTimeout(() => {
          getBankChangeApprovalStatus();
          startLoop();
        }, 15000);
      };
      startLoop();
    }

    return () => clearTimeout(timeoutId)
  }, [bankChangeApprovalPoll])

  const getLatestLead = async () => {
    setSaving(true)
    try {
      // @TODO: Should this be part of the route or a query param?
      const leadGuid = queryParams.get('lead');
      if (leadGuid) {
        requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification/${params.id}/lead/${leadGuid}`)
      } else {
        requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification/${params.id}/latest_lead`)
      }
      const response = await requests.get()
      setVerificationFields(response?.data?.data?.verification_fields)
      setPaymentMethods(response?.data?.data?.payment_method_dropdown_options)
      setPaymentScheduleDetails(response?.data?.data?.payment_schedule_details)
      setOriginalEffectiveDate(response?.data?.data?.original_effective_date)
      setOriginalLoanFundingMethodId(response?.data?.data?.original_loan_funding_method_dropdown_option_id)
      setLatestVerificationBankAccountId(response?.data?.data?.latest_verification_bank_account_id)
      setPromotionName(response?.data?.data?.promotion_name)
      setCustomerData(response?.data?.data?.customer)
      setCurrentPtiTerms(response?.data?.data?.pti_terms)
      setCanStartVerification(response?.data?.data?.can_start_verification)

      setManagerApproval(response?.data?.data?.manager_approval)
      const managerApprovalStatus = dropdownOptions.find((o) => o?.id === response?.data?.data?.manager_approval?.mgr_approval_status_dropdown_option_id)?.code_key
      if (managerApprovalStatus === 'mgr_approval_status_required') {
        setManagerApprovalPoll(true)
      }
      await getBanners('customer', response?.data?.data?.customer?.id);

      if (response?.data?.data?.is_verified && isReadOnly && !verificationId) {
        setIsVerified(true)
        return processVerificationQuestions()
      } else if (canStartVerification) {
        setIsReadOnly(false)
      }

      setSaving(false)
    } catch (error) {
      setSaving(false)
      if (error?.response?.status === 401 || error?.response?.status === 404) {
        navi('/404')
      } else {
        dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
      }
    }
  }

  const getPrograms = async () => {
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification/${customerData.id}/programs`)
      const response = await requests.get()
      setPrograms(response?.data?.data)
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    }
  }

  const getBankChangeApprovalStatus = async () => {
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification/${customerData.id}/bank_approval_status`)
      const response = await requests.get()

      const bankChangeApprovalRecord = response?.data?.data
      const bankChangeApprovalStatus = dropdownOptions.find((o) => o?.id === bankChangeApprovalRecord?.bank_change_outcome_dropdown_option_id)?.code_key
      if (bankChangeApprovalStatus !== 'bank_change_outcome_duplicate_pending') {
        if (step == 'questions') {
          await getLatestLead()
        }
        dispatch(triggerRerender({ subpanelId: 'bank_change_dashlet' }));
        setBankChangeApprovalPoll(false)
      }

      setBankChangeApproval(bankChangeApprovalRecord)
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    }
  }

  const getManagerApprovalStatus = async () => {
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification/${customerData.id}/manager_approval_status`)
      const response = await requests.get()

      const managerApprovalRecord = response?.data?.data
      const managerApprovalStatus = dropdownOptions.find((o) => o?.id === managerApprovalRecord?.mgr_approval_status_dropdown_option_id)?.code_key
      if (managerApprovalStatus !== 'mgr_approval_status_required') {
        if (step == 'questions') {
          await getLatestLead()
        } else if (step == 'payment_schedule' && managerApprovalStatus !== 'mgr_approval_status_approved') {
          await getLatestLead()
        }
        dispatch(triggerRerender({ subpanelId: 'bank_change_dashlet' }));
        setManagerApprovalPoll(false)
      }

      setManagerApproval(managerApprovalRecord)
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    }
  }

  const getNextPaymentDates = async () => {
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification/${params.id}/next_payment_dates`)
      const response = await requests.get()
      setNextPaymentDatesTooltip(`Are your next two paydays ${response.data[0]} and ${response.data[1]}?`)
    } catch (error) {
      setNextPaymentDatesTooltip("Unable to determine next two paydays.")
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    }
  }

  const startLeadVerification = async () => {
    setSaving(true)
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification/${customerData.id}/start_verification`)
      const response = await requests.post()
      setVerificationId(response?.data?.verification_id)
      userStartedVerificationId.current = response?.data?.verification_id // Store the user-started verification ID
      setStartVerification(true)
      setSaving(false)
      setLockNotificationText('Locked by ' + active.data.label + ', expiring at ' + FormatISODateTime(new Date(Date.now() + 5 * 60000)))
      setLockAgentUserId(active.data.id)
      setDisplayLockNotice(true)
      setIsReadOnly(false)
    } catch (error) {
      setSaving(false)
      setStartVerification(false)
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    }
  }

  const stopLeadVerification = async () => {
    setSaving(true)
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification/${customerData.id}/stop_verification`)
      await requests.post([])
      setVerificationId(0)
      userStartedVerificationId.current = null // Clear the user-started verification ID
      if (step === 'loan_agreement') {
        setStep('questions')
      }
      setStartVerification(false)
      setSaving(false)
      setDisplayLockNotice(false)
      if (isVerified) {
        setIsReadOnly(true)
      }
    } catch (error) {
      setVerificationId(0)
      userStartedVerificationId.current = null // Clear the user-started verification ID
      setSaving(false)
      setDisplayLockNotice(false)
      setStartVerification(false)
      setIsReadOnly(true)
      if (error?.response?.status == 409) {
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'warning', body: errorMessage }))
      } else {
        dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
      }
    }
  }

  /**
   * When the agent completes verification with the customer they will click the
   *  complete verification button. This will attribute that lead to the Agent.
   * @returns {Promise<void>}
   */
  const completeVerification = async () => {
    setSaving(true)
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification/${customerData.id}/complete_verification`)
      await requests.post([])
      setVerificationId(0)
      userStartedVerificationId.current = null // Clear the user-started verification ID
      setStep('questions')
      setStartVerification(false)
      setSaving(false)
      setDisplayLockNotice(false)
      setIsReadOnly(true)
    } catch (error) {
      setSaving(false)
      setStartVerification(true)
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    }
  }

  const slideDownHistory = () => {
    if (modal.is_active === false) {
      dispatch(
        setModal({ is_active: true, title: `Verification History`, content: `${process.env.REACT_APP_API_DOMAIN}/manual_verification/${customerData.id}/verification_history`, type: 'history' })
      )
    } else {
      dispatch(setModal({ title: `${module.name} History`, content: '', type: '', is_active: false }))
    }
  }

  const submitUnlock = async (lock) => {
    if (lock && displayLockNotice) {
      return null;
    }
    requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${params.id}/lock`);
    try {
      if (!lock) {
        setSaving(true)
      }
      let body = { lock: lock }
      const response = await requests.modify(body)
      if (response.status === 200) {
        setDisplayLockNotice(lock ? true : false)

        if (response.data.customer_locked) {
          setLockNotificationText('Locked by ' + response.data.lock_details.lock_agent_name + ', expiring at ' + FormatDateTimeToSame(response.data.lock_details.lock_expiration_date, 'MM/dd/yyyy hh:mm:ss a'))
          setLockAgentUserId(active.data.id)
        } else {
          setLockAgentUserId(null)
          setLockNotificationText(null)
        }
        setSaving(false)
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        setSaving(false)
      }
    } catch (error) {
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const showLockNotice = () => {
    const showUnlockButton = forceRecordUnlockPermission || lockAgentUserId === active.data.id;
    return (
      <LockNotice
        content={lockNotificationText}
        showUnlockButton={showUnlockButton}
        submitUnlock={submitUnlock}
        saving={saving}
        setLockCheck={setLockCheck}
        customerId={params.id}
        setLockNotificationText={setLockNotificationText}
        setLockAgentUserId={setLockAgentUserId}
        displayLockNotice={displayLockNotice}
        lockCheck={lockCheck}
        setDisplayLockNotice={setDisplayLockNotice}
        requests={requests}
      />
    );
  }

  // Updates the verificationFields state with new key-value pairs.
  // Ensures state consistency during rapid successive updates.
  const updateVerificationFields = (newKey, newData) => {
    setVerificationFields((prevFields) => {
      return {
        ...prevFields,
        [newKey]: newData
      };
    });
  };

  const getButtonText = () => {
    if (!customerData?.id) {
      return 'Loading data';
    }
    if (saving) {
      return 'Starting';
    }
    return 'Start Verification';
  };

  const processVerificationQuestions = async () => {
    setSaving(true)
    requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification/${params.id}/process_verification_questions?is_read_only=${isReadOnly ? 1 : 0}`)
    try {
      const response = await requests.get()
      if (response.status === 200 || response.status === 201) {
        setSaving(false)
        const responseData = response.data.data
        if (responseData.is_verified) {
          setIsVerified(true)
          setPaymentScheduleData(responseData)
          setPtiTerms(responseData.pti_terms)
          setPaymentScheduleDetails(responseData.payment_schedule_details)
          setOriginalEffectiveDate(responseData.original_effective_date)
          setOriginalLoanFundingMethodId(responseData.original_loan_funding_method_dropdown_option_id)
          setLatestVerificationBankAccountId(responseData.latest_verification_bank_account_id)
          // Lets not over-write the user-started verification ID if it exists
          if (userStartedVerificationId.current) {
            // If user started verification, preserve their verification ID
            setVerificationId(userStartedVerificationId.current)
          } else {
            // Only set from backend if no user-started ID and either no current ID or in read-only mode
            setVerificationId(responseData.verification_id)
          }
          setStep('payment_schedule')
          setIsPrequalified(responseData.is_prequalified)
          setPrequalifiedData(responseData.prequalified_data)
          dispatch(setNotification({ type: 'success', body: '' }))
          setIsRealtimeFundingAvailable(responseData.is_realtime_funding_available)
        } else {
          setIsReadOnly(false)
          dispatch(setNotification({ type: 'error', body: 'Verification Data Incomplete' }))
        }
      } else {
        setSaving(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      setSaving(false)
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    }
  };

  const handleBankChange = (newBankData) => {
    setCustomerData((prevData) => ({
      ...prevData,
      ...newBankData
    }))
  }

  return (
    <LayoutSidebar
      id={customerData.id}
      portfolioLabel={customerData?.portfolio_label ?? null}
      contentTitle={customerData.first_name ? customerData.first_name + " " + customerData.last_name : 'Verification'}
      isLoadingBanners={isLoadingBanners}
      banners={banners}
      actionButtons={(
        <>
          <CustomerServiceButtons customer={customerData} module="manual_verification" disableEdit={displayLockNotice && lockAgentUserId !== active.data.id} submitUnlock={submitUnlock}>
            <NoteButton />
          </CustomerServiceButtons>
          <div className="header-button-sub-container mr-1">
            <button
              type="button"
              className="button has-background-grey has-text-white is-small header-button-icon"
              onClick={() => {
                navi(`/customer/${params.id}`)
              }}
            >
              <span className="mx-2">Customer Details</span>
            </button>
            {startVerification ? (
              <button
                type="button"
                className="button has-background-link has-text-white is-small header-button-icon"
                onClick={() => stopLeadVerification()}
                disabled={saving}
              >
                <span className="mx-2">
                  {saving ? 'Stopping' : 'Stop Verification'}
                  <span
                    className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                    data-tooltip={`Will stop the manual verification process and allow the customer to take action in SSV.
                    They will return to the last step in SSV that they completed. This will not include any changes made with an Agent.
                    In general once in manual verification, the customer should stay there.`}
                  >
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className={`pl-1`}
                    />
                  </span>
                </span>
              </button>
            ) : (
              <button
                type="button"
                className="button has-background-link has-text-white is-small header-button-icon"
                data-testid="start-verification-button"
                onClick={() => startLeadVerification()}
                disabled={(displayLockNotice && lockAgentUserId !== active.data.id) || verificationId || saving || !customerData?.id || !canStartVerification}
              >
                <span className="mx-2">
                  {getButtonText()}
                  <span
                    className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                    data-tooltip={!canStartVerification ? 'Sorry, this lead is not a valid lead for manual verification.' : 'Will start the manual verification process by creating a new verification record and block the customer from taking any action in SSV.'}
                  >
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className={`pl-1`}
                    />
                  </span>
                </span>
              </button>
            )}
          </div>
          <div className="header-button-sub-container">
            <button
              type="button"
              className="button has-background-grey has-text-white is-small mr-1 header-button-icon"
              onClick={slideDownHistory}
            >
              <span className="mx-2">
                <FontAwesomeIcon icon={faHistory} className="pr-1" />
                History
              </span>
            </button>
          </div>
        </>
      )}
      content={(
        <>
          {showLockNotice()}
          {Object.keys(paymentScheduleDetails).length === 0 && Object.keys(customerData).length === 0 && (
            <div className="loader-wrapper-no-background">
              <Loading />
            </div>
          )}
          {Object.keys(paymentScheduleDetails).length > 0 && Object.keys(customerData).length > 0 && (
            <>
              {step === 'questions' && (
                <VerificationQuestions
                  setStep={setStep}
                  verificationFields={verificationFields}
                  latestVerificationBankAccountId={latestVerificationBankAccountId}
                  startVerification={startVerification}
                  verificationFieldsObject={verificationFieldsObject}
                  verificationId={verificationId}
                  setVerificationId={setVerificationId}
                  customerData={customerData}
                  updateVerificationFields={updateVerificationFields}
                  setPtiTerms={setPtiTerms}
                  setPaymentScheduleData={setPaymentScheduleData}
                  setPaymentScheduleDetails={setPaymentScheduleDetails}
                  currentPtiTerms={currentPtiTerms}
                  managerApproval={managerApproval}
                  setManagerApprovalPoll={setManagerApprovalPoll}
                  getManagerApprovalStatus={getManagerApprovalStatus}
                  bankChangeApproval={bankChangeApproval}
                  setBankChangeApprovalPoll={setBankChangeApprovalPoll}
                  getBankChangeApprovalStatus={getBankChangeApprovalStatus}
                  processVerificationQuestions={processVerificationQuestions}
                  isReadOnly={isReadOnly}
                  getNextPaymentDates={getNextPaymentDates}
                  nextPaymentDatesTooltip={nextPaymentDatesTooltip}
                  setIsPrequalified={setIsPrequalified}
                  setPrequalifiedData={setPrequalifiedData}
                />
              )}
              {step === 'payment_schedule' && (
                <VerificationPaymentSchedule
                  setStep={setStep}
                  customerData={customerData}
                  programs={programs}
                  paymentScheduleDetails={paymentScheduleDetails}
                  setLoanData={setLoanData}
                  paymentMethods={paymentMethods}
                  ptiTerms={ptiTerms}
                  setPtiTerms={setPtiTerms}
                  paymentScheduleData={paymentScheduleData}
                  setPaymentScheduleData={setPaymentScheduleData}
                  dropdownOptions={dropdownOptions}
                  verificationId={verificationId}
                  managerApproval={managerApproval}
                  setManagerApproval={setManagerApproval}
                  setManagerApprovalPoll={setManagerApprovalPoll}
                  isReadOnly={isReadOnly}
                  isPrequalified={isPrequalified}
                  prequalifiedData={prequalifiedData}
                  isRealtimeFundingAvailable={isRealtimeFundingAvailable}
                  setIsRealtimeFundingAvailable={setIsRealtimeFundingAvailable}
                  originalEffectiveDate={originalEffectiveDate}
                  originalLoanFundingMethodId={originalLoanFundingMethodId}
                />
              )}
              {step === 'loan_agreement' && (
                <VerificationLoanSigning
                  setStep={setStep}
                  customerData={customerData}
                  loanData={loanData}
                  paymentScheduleData={paymentScheduleData}
                  dropdownOptions={dropdownOptions}
                  completeVerification={completeVerification}
                />
              )}

              <div className={`lox-sub-panel-group columns is-multiline pt-3 pr-3`}>
                <Suspense fallback={<div />}>
                  {module?.module_related_modules?.map((m) => {
                    const subModule = modules.data.find(
                      (e) => e.code_key === m.subpanel_module_code_key
                    )
                    if (subModule && hasUserPermission(userPermissions, `${subModule.code_key}_read`)) {
                      return (
                        <ModuleSubPanel
                          id={params?.id}
                          name={"Customer"}
                          codeKey={"customer"}
                          module={parentModule}
                          subModule={subModule}
                          key={m.subpanel_module_code_key}
                          hidden={false}
                          isPagination={m.is_pagination}
                          isOrderable={m.is_orderable}
                          isInlineEditable={m.is_inline_editable}
                          isMappingTable={m.is_mapping_table}
                          parentModuleId={params.id}
                          parentModuleData={null}
                        />
                      )
                    }
                  })}
                </Suspense>
              </div>
            </>
          )}
        </>
      )}
      dashlets={(
        <>
          {Object.keys(customerData).length > 0 && (
            <PortfolioDashlet key={'portfolio'} endpointPath={`${process.env.REACT_APP_API_DOMAIN}/customer/${customerData?.id}/portfolio/dashlet`} />
          )}
          <HolidayDashlet dropdownOptions={dropdownOptions} dropdowns={dropdowns} />
          {Object.keys(customerData).length > 0 && (
            <>
              <VerificationDashlet
                key={'verification'}
                dropdownOptions={dropdownOptions}
                customerId={customerData && customerData?.id}
                isLocked={displayLockNotice && lockAgentUserId !== active.data.id}
                setVerificationId={setVerificationId}
                setStartVerification={setStartVerification}
                setStep={setStep}
              />
              <LeadSignatureDashlet key={'lead-signature'} customerId={customerData?.id} />
              <BankChangeDashlet
                key={'bank_change'}
                endpointPath={`${process.env.REACT_APP_API_DOMAIN}/customer/${customerData?.id}/bank_change/dashlet`}
                dropdownOptions={dropdownOptions}
                onBankChange={handleBankChange}
                currentStep={step}
              />
            </>
          )}
          <NoteDashlet key={'note'} />
        </>
      )}
    />
  )
}
